import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { DynamicIcon } from './icons';
import { useGlobalSearch } from '../hooks/useGlobalSearch';

const SCOPE_MAPPING = {
  u: 'users',
  d: 'dashboards',
  f: 'flows',
  t: 'tenants',
  a: 'apps',
  p: 'datasets',
};

const SCOPE_LABELS = {
  users: 'Users',
  dashboards: 'Dashboards',
  flows: 'Flows',
  tenants: 'Tenants',
  apps: 'Apps',
  datasets: 'Datasets',
};

export default function GlobalSearchPalette({ onNavigate, quickActions = [] }) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [scope, setScope] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const inputRef = useRef(null);
  const { results, isLoading, error } = useGlobalSearch(query, scope);
  const navigate = useNavigate();

  // Combine backend results with quick actions (if they match the query)
  const filteredQuickActions = query.trim() !== '' && !scope
    ? quickActions.filter(action =>
      action.title.toLowerCase().includes(query.toLowerCase()) ||
      action.description.toLowerCase().includes(query.toLowerCase())
    )
    : [];

  // If no query, show a few top quick actions. Otherwise show filtered actions + backend results
  const displayResults = query.trim() === ''
    ? quickActions.slice(0, 5)
    : [...filteredQuickActions, ...results];

  // Keyboard shortcut listener (Ctrl+K or Cmd+K)
  useEffect(() => {
    let ctrlKPressed = false;
    let timer = null;

    const handleGlobalKeyDown = (e) => {
      // Handle the initial Ctrl+K
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
        ctrlKPressed = true;

        // Allow a short window (1000ms) to press a modifier key like 'u'
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          ctrlKPressed = false;
        }, 1000);
        return;
      }

      // Handle chord modifier (e.g., Ctrl+K, then 'u')
      if (ctrlKPressed && !e.ctrlKey && !e.metaKey && SCOPE_MAPPING[e.key.toLowerCase()]) {
        e.preventDefault();
        setScope(SCOPE_MAPPING[e.key.toLowerCase()]);
        ctrlKPressed = false;
        return;
      }

      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
        setScope(null);
      }
    };

    const handleOpenEvent = () => setIsOpen(true);

    window.addEventListener('keydown', handleGlobalKeyDown);
    window.addEventListener('open-global-search', handleOpenEvent);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown);
      window.removeEventListener('open-global-search', handleOpenEvent);
    };
  }, []);

  // Auto-focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, scope, results]);

  // Handle keyboard navigation within the palette
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < displayResults.length - 1 ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : displayResults.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (displayResults[selectedIndex]) {
        handleSelect(displayResults[selectedIndex]);
      }
    } else if (e.key === 'Backspace' && query === '') {
      // Remove scope if backspace pressed on empty query
      if (scope) {
        setScope(null);
      }
    }
  };

  const handleSelect = (item) => {
    setIsOpen(false);
    setQuery('');
    setScope(null);
    if (onNavigate) {
      onNavigate(item);
    } else if (item.url_path) {
      navigate(item.url_path);
    }
  };

  // Allow typed commands like `/u ` to set scope
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (val.startsWith('/') && val.length === 2 && val[1] !== ' ') {
      const char = val[1].toLowerCase();
      if (SCOPE_MAPPING[char]) {
        setScope(SCOPE_MAPPING[char]);
        setQuery('');
        return;
      }
    }
    setQuery(val);
  };

  if (!isOpen) return null;

  const getIconForType = (type, fallbackIcon) => {
    if (fallbackIcon) return fallbackIcon;
    switch (type) {
      case 'user': return 'User';
      case 'dashboard': return 'LayoutDashboard';
      case 'flow': return 'GitBranch';
      case 'tenant': return 'Globe';
      case 'app': return 'Package';
      case 'dataset': return 'Database';
      case 'action': return 'Zap';
      default: return 'File';
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] sm:pt-[18vh] px-4">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={() => setIsOpen(false)}
      />

      <div className="relative w-full max-w-3xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800 overflow-hidden transform transition-all">

        {/* Search Header */}
        <div className="flex items-center px-5 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <DynamicIcon name="Search" className="w-6 h-6 text-gray-400 mr-4 shrink-0" />

          {scope && (
            <div className="flex items-center bg-primary/10 text-primary px-2.5 py-1.5 rounded-md text-sm font-semibold mr-3 border border-primary/20">
              {SCOPE_LABELS[scope]}
              <button
                onClick={() => setScope(null)}
                className="ml-1.5 hover:text-primary/70 transition-colors focus:outline-none"
              >
                <DynamicIcon name="X" className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          <input
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent border-none focus:outline-none focus:ring-0 text-gray-800 dark:text-gray-100 text-xl font-medium placeholder-gray-400"
            placeholder={scope ? "Search " + SCOPE_LABELS[scope].toLowerCase() + "..." : "Search anything... (Try /u for users)"}
            value={query}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
          />

          {isLoading && (
            <DynamicIcon name="Loader2" className="w-5 h-5 text-gray-400 animate-spin ml-3" />
          )}

          <div className="flex items-center space-x-1.5 ml-4 text-[11px] text-gray-400 font-mono">
            <span className="px-1.5 py-0.5 rounded border border-gray-200 dark:border-gray-700">ESC</span>
            <span>to close</span>
          </div>
        </div>

        {/* Results List */}
        <div className="max-h-[60vh] overflow-y-auto overscroll-contain pb-2">
          {displayResults.length > 0 ? (
            <ul className="py-2">
              {displayResults.map((item, index) => {
                const selected = index === selectedIndex;
                return (
                  <li
                    key={item.id}
                    className={`
                      px-5 py-3 flex items-center cursor-pointer border-l-2
                      ${selected
                        ? 'bg-primary/5 border-primary text-primary dark:bg-primary/10 dark:text-blue-400'
                        : 'border-transparent text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'}
                    `}
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className={`p-2 rounded-lg mr-4 shrink-0 ${selected ? 'bg-primary/10 text-primary' : 'bg-gray-100 dark:bg-gray-800 text-gray-500'}`}>
                      <DynamicIcon name={getIconForType(item.type, item.icon)} className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[15px] font-medium truncate">{item.title}</span>
                        <span className="text-[11px] uppercase tracking-wider font-semibold text-gray-400 shrink-0 ml-3">
                          {item.type === 'action' ? 'Action' : item.type}
                        </span>
                      </div>
                      {item.description && (
                        <p className={`text-[13px] truncate mt-0.5 ${selected ? 'text-primary/70 dark:text-blue-300/70' : 'text-gray-500'}`}>
                          {item.description}
                        </p>
                      )}
                    </div>
                    {selected && (
                      <DynamicIcon name="CornerDownLeft" className="w-5 h-5 ml-4 opacity-50 shrink-0" />
                    )}
                  </li>
                );
              })}
            </ul>
          ) : (
            query.length > 1 && !isLoading ? (
              <div className="py-16 text-center">
                <DynamicIcon name="SearchX" className="w-14 h-14 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-base">No results found for "{query}"</p>
              </div>
            ) : query.length === 0 ? (
              <div className="px-5 py-4">
                <div className="text-[11px] font-bold text-gray-400 uppercase tracking-widest mb-3 px-2">Tips</div>
                <div className="text-sm text-gray-500 space-y-3 px-2">
                  <div className="flex items-center">
                    <span className="w-7 font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded mr-3 text-center">/u</span>
                    <DynamicIcon name="User" className="w-4 h-4 mr-2 opacity-70" />
                    Scope to Users
                  </div>
                  <div className="flex items-center">
                    <span className="w-7 font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded mr-3 text-center">/d</span>
                    <DynamicIcon name="LayoutDashboard" className="w-4 h-4 mr-2 opacity-70" />
                    Scope to Dashboards
                  </div>
                  <div className="flex items-center">
                    <span className="w-7 font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded mr-3 text-center">/f</span>
                    <DynamicIcon name="GitBranch" className="w-4 h-4 mr-2 opacity-70" />
                    Scope to Flows
                  </div>
                  <div className="flex items-center">
                    <span className="w-7 font-mono text-gray-400 bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded mr-3 text-center">/t</span>
                    <DynamicIcon name="Globe" className="w-4 h-4 mr-2 opacity-70" />
                    Scope to Tenants
                  </div>
                </div>
              </div>
            ) : null
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex justify-between items-center text-[11px] text-gray-400">
          <div className="flex items-center space-x-3">
            <span className="flex items-center"><DynamicIcon name="ArrowUp" className="w-3 h-3 mr-1" /> <DynamicIcon name="ArrowDown" className="w-3 h-3 mr-1" /> Navigate</span>
            <span className="flex items-center"><DynamicIcon name="CornerDownLeft" className="w-3 h-3 mr-1" /> Select</span>
          </div>
        </div>
      </div>
    </div>
  );
}
